import { useSyncExternalStore } from "react";
import { safeStorage } from "../lib/safeStorage";

const KEY = "lura.web.appearance";
const CHANGE = "lura:appearance";
type Theme = "light" | "dark";
const valid = (value: unknown): value is Theme => value === "light" || value === "dark";
function systemTheme(): Theme {
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}
function preference(): Theme | null {
  const value = safeStorage.local.get(KEY);
  return valid(value) ? value : null;
}
function apply(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
}
/** Public, origin-local display preference. No identity or network dependency. */
export function initializeAppearance() { apply(preference() ?? systemTheme()); }
function snapshot(): Theme {
  const value = document.documentElement.dataset.theme;
  return valid(value) ? value : systemTheme();
}
function subscribe(notify: () => void) {
  const media = typeof window.matchMedia === "function" ? window.matchMedia("(prefers-color-scheme: dark)") : null;
  const sync = () => { initializeAppearance(); notify(); };
  const storage = (event: StorageEvent) => { if (event.key === KEY || event.key === null) sync(); };
  window.addEventListener(CHANGE, notify);
  window.addEventListener("storage", storage);
  media?.addEventListener("change", sync);
  return () => {
    window.removeEventListener(CHANGE, notify);
    window.removeEventListener("storage", storage);
    media?.removeEventListener("change", sync);
  };
}
export function WebHeader({ locale, as: Tag = "header", className = "", markClassName = "" }: {
  locale: "en" | "es"; as?: "header" | "div"; className?: string; markClassName?: string;
}) {
  const theme = useSyncExternalStore(subscribe, snapshot);
  const next = theme === "dark" ? "light" : "dark";
  const label = locale === "es" ? (next === "dark" ? "Oscuro" : "Claro") : (next === "dark" ? "Dark" : "Light");
  return <Tag className={`v3-header ${className}`}>
    <img className={markClassName} src={theme === "dark" ? "/wordmark-dark.png" : "/wordmark-light.png"} alt="Lura" width={400} height={156} />
    <button type="button" className="v3-theme" aria-label={`${locale === "es" ? "Apariencia" : "Appearance"}: ${label}`} onClick={() => {
      apply(next);
      // The page still changes when storage is unavailable.
      safeStorage.local.set(KEY, next);
      window.dispatchEvent(new Event(CHANGE));
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
        {next === "dark" ? <path d="M20.8 13.2A9 9 0 0 1 10.8 3.1 9 9 0 1 0 20.8 13.2Z" /> : <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1.5 1.5m11 11L19 19M5 19l1.5-1.5m11-11L19 5" /></>}
      </svg><span>{label}</span>
    </button>
  </Tag>;
}
